import React, { useEffect, useMemo, useState } from 'react';
import Table from "./Table";
import LoadingAnimations from "../Loaders/Skeleton";
import { dateTimeToString } from "../../utilities/dateTime";

// smsIntentBadge renders a small inline coloured badge for an AI SMS intent
// classification attached to an incoming-SMS phone-log row (FOU-636).
// agreement=green, cancellation=amber, gate_code=blue, other=grey. Rendered
// only when the row carries an smsIntent. The smsIntent payload is populated
// server-side by AttachSmsIntents, which is itself entitlement-gated (only
// runs for users with the sms_intent entitlement) — so non-entitled users
// never receive an smsIntent field and the badge can never render for them.
// The badge is therefore already entitlement-gated; no client-side gate needed.
const smsIntentBadge = (smsIntent) => {
  if (!smsIntent) {
    return null;
  }
  // Below the server-side confidence threshold (smsIntentSignalThreshold
  // in database/db.go): show a neutral grey 'Unable to determine intent'
  // badge with no intent label, no confidence percentage, and no colour. The
  // raw classification is still carried in the struct (and stored in the
  // history row) for calibration, but we do not surface the intent to the
  // user. Per rev 11 (FOU-636), belowThreshold does NOT suppress the action
  // badge: a high-confidence action is surfaced independently of the intent
  // confidence (the 'Come anytime' stage case — intent torn at 0.36 but action
  // confident at 0.94). Per rev 12 (FOU-636), the needs-reply badge is ALSO
  // self-gated (its own confidence only) — a torn intent no longer suppresses
  // it: a message can be too ambiguous to classify yet obviously need a reply
  // ('Perfect. Do you have a window of time, approximate?' — intent 0.68,
  // needs_reply 0.96). All three badges render independently per their flags.
  const belowThreshold = !!smsIntent.belowThreshold;

  // Human-readable label + tailwind colour for the intent choice, and the
  // grey-unable badge for the below-threshold path.
  const labelMap = {
    agreement: 'Agreement',
    cancellation: 'Cancellation',
    gate_code: 'Gate Code',
  };
  const colourMap = {
    agreement: 'tw-border-green-300 tw-text-green-800 tw-bg-green-50',
    cancellation: 'tw-border-amber-300 tw-text-amber-800 tw-bg-amber-50',
    gate_code: 'tw-border-blue-300 tw-text-blue-800 tw-bg-blue-50',
  };
  let intentBadge;
  if (belowThreshold) {
    intentBadge = 
      <span className="tw-ml-2 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-border tw-inline-block tw-border-gray-300 tw-text-gray-600 tw-bg-gray-50">
        Unable to determine intent
      </span>
    ;
  } else {
    const intent = smsIntent.intent || '';
    const confidence = typeof smsIntent.confidence === 'number'
      ? Math.round(smsIntent.confidence * 100)
      : null;
    const label = labelMap[intent] || intent.charAt(0).toUpperCase() + intent.slice(1);
    const colour = colourMap[intent] || 'tw-border-gray-300 tw-text-gray-700 tw-bg-gray-50';
    const text = confidence !== null
      ? `AI: ${label} ${confidence}%`
      : `AI: ${label}`;
    intentBadge = 
      <span className={`tw-ml-2 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-border tw-inline-block ${colour}`}>
        {text}
      </span>
    ;
  }
  // Second badge: a blue ACTIONS badge for the model's action choice
  // (answers.action.choice) when smsIntent.actionBadge is true. Added in rev 9
  // (Colin's ACTIONS badge) alongside the existing intent question, which is
  // unchanged — action is a separate question, not a replacement for intent.
  // Rendered ONLY for the concrete actionable choices (clarify/none get no
  // badge server-side) and only when action confidence crossed
  // smsIntentSignalThreshold. Per rev 11 a belowThreshold row no longer
  // suppresses this — the action badge renders alongside grey-unable when the
  // server set it. This block is shared by both the below-threshold and
  // above-threshold paths (no early return above).
  const actionLabelMap = {
    set_appointment_all_day: 'Set appointment - all day',
    set_appointment_specific: 'Set appointment - time',
    set_gate_code: 'Set gate code',
    reminder_call_before: 'Reminder: call before',
    reschedule_request: 'Reschedule',
  };
  const actionBadge = smsIntent.actionBadge ?
    <span className="tw-ml-1 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-border tw-inline-block tw-border-blue-300 tw-text-blue-800 tw-bg-blue-50">
      {actionLabelMap[smsIntent.action] || smsIntent.action}
    </span>
    : null;
  // Third badge: a separate amber "Needs reply" signal when the model's
  // needs_reply noul score crossed smsIntentSignalThreshold server-side. This
  // surfaces compound messages that agree AND ask a question, which the intent
  // badge alone would collapse to a single "Agreement". Rendered ONLY when
  // smsIntent.needsReplyBadge is true. Per rev 11 the server sets
  // NeedsReplyBadge=false for belowThreshold rows (needs-reply is coupled to
  // the intent — a 'Needs reply' on a message we cannot classify adds noise),
  // so no needs-reply badge renders alongside grey-unable. Order: intent
  // badge, then action badge, then needs-reply badge — all three render
  // independently so a compound message can show all that apply.
  const needsReplyBadge = smsIntent.needsReplyBadge ? 
    <span className="tw-ml-1 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-border tw-inline-block tw-border-amber-300 tw-text-amber-800 tw-bg-amber-50">
      Needs reply
    </span>
    : null;
  return (
    <span>
      {intentBadge}
      {actionBadge}
      {needsReplyBadge}
    </span>
  );
};

// canClassifyRow returns true for an inbound phone-log row that does not
// already carry an sms-intent classification — i.e. a row eligible for the
// per-message Classify button (FOU-636 rev 2).
//
// Full gating chain for the Classify button (rev 4): the button only renders
// when ALL of the following hold —
//   1. Row shape: canClassifyRow is true (inbound phone-log, Section='phone-log*',
//      Description='Received Text*', and no existing smsIntent badge).
//   2. Entitlement: the logged-in user holds the sms_intent entitlement
//      (config.smsIntentEntitled, stamped server-side from entitlements.Can).
//   3. Feature flag: the arcpro-sms-intent-classification Unleash flag is on
//      (config.smsIntentClassifyEnabled, stamped server-side from flags.F).
// Conditions 2 and 3 are enforced by the page (InspectionPreContact), which
// only passes onClassify down when BOTH are true; this table gates rendering
// on typeof onClassify === 'function', so the button never appears unless the
// page handed down a real handler. The backend re-checks the flag in
// ClassifySMSMessage as defence-in-depth.
const canClassifyRow = (row) => {
  if (!row) {return false;}
  const section = row.Section || '';
  const description = row.Description || '';
  return section.indexOf('phone-log') === 0
    && description.indexOf('Received Text') === 0
    && !row.smsIntent;
};

export default function InspectionHistoryTable({ items, onClassify }) {

  const [data, setData] = useState(null);
  // Tracks which history row ids have a Classify request in flight, so the
  // button can disable itself while pending.
  const [classifying, setClassifying] = useState({});

  useEffect(() => {
    if (items === null) {
      return;
    }

    // Append Download and Delete buttons
    const newItems = items.map((item) => {
      item.time = dateTimeToString(new Date(item.HistoryTime));
      if (item.User.Id === 0) {
        item.User.Name = "System";
      }
      return item;
    });
    setData(newItems);
  }, [items]);

  const columns = useMemo(() => [
    { Header: "Time", accessor: "time", minWidth: 140 },
    { Header: "User", accessor: "User.Name", minWidth: 170 },
    { Header: "Description", Cell: props => {
      const desc = props.row.original.Description.replaceAll("\\n", " ");
      const badge = smsIntentBadge(props.row.original.smsIntent);
      // showClassify: row-shape gate (canClassifyRow) AND the page passed a
      // real onClassify handler — which it only does when the user holds the
      // sms_intent entitlement AND the arcpro-sms-intent-classification flag
      // is on (both stamped server-side into reactData). See canClassifyRow
      // comment for the full chain.
      const showClassify = canClassifyRow(props.row.original)
          && typeof onClassify === 'function';
      const rowId = props.row.original.Id;
      const isClassifying = !!classifying[rowId];
      const handleClassify = async () => {
        if (isClassifying) {return;}
        setClassifying(prev => ({ ...prev, [rowId]: true }));
        try {
          await onClassify(rowId);
        } finally {
          setClassifying(prev => {
            const next = { ...prev };
            delete next[rowId];
            return next;
          });
        }
      };
      return (
        <span>
          {desc}
          {badge}
          {showClassify && 
              <button
                type="button"
                onClick={handleClassify}
                disabled={isClassifying}
                className="tw-ml-2 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-border-gray-400 hover:tw-text-gray-800 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                {isClassifying ? 'Classifying…' : 'Classify'}
              </button>
          }
        </span>
      );
    }
    }
  ], [onClassify, classifying]);

  if (data === null) {
    return <LoadingAnimations height={40}/>;
  }

  return (
    <Table columns={columns} data={data}/>
  );
}